import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  FolderPlus, Loader2, Sparkles, UserPlus, ClipboardCheck,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import FilePickerButton from "../ui/FilePickerButton";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEZIONE_LABEL = {
  anagrafica: "Anagrafica",
  sanitaria: "Sanitaria",
  legali: "Documenti legali",
  generale: "Generale",
  amministrativa: "Amministrativa",
};

const DOCUMENTI_CATALOG = [
  { key: "carta_identita_ospite", label: "Carta d'identità ospite" },
  { key: "tessera_sanitaria", label: "Tessera sanitaria" },
  { key: "verbale_invalidita", label: "Verbale invalidità" },
  { key: "piano_terapeutico", label: "Piano terapeutico" },
  { key: "relazione_sanitaria", label: "Relazione sanitaria" },
  { key: "esami_recenti", label: "Esami recenti" },
  { key: "impegnativa_medico", label: "Impegnativa medico" },
  { key: "contratto_amm_sostegno", label: "Contratto amm. di sostegno" },
  { key: "certificazione_alzheimer", label: "Certificazione Alzheimer" },
  { key: "dichiarazione_redditi", label: "Ultima dichiarazione dei redditi" },
  { key: "altro", label: "Altro documento" },
];

function isoDateOffset(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function splitNomeOspite(nomeOspite) {
  const parts = String(nomeOspite || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { nome: "", cognome: "" };
  if (parts.length === 1) return { nome: parts[0], cognome: "" };
  return { nome: parts.slice(0, -1).join(" "), cognome: parts[parts.length - 1] };
}

export default function ConfermaIngressoDialog({ open, onOpenChange, richiesta, onSuccess }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [statoOspite, setStatoOspite] = useState("PRE_INGRESSO");
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [sesso, setSesso] = useState("");
  const [dataNascita, setDataNascita] = useState("");
  const [codiceFiscale, setCodiceFiscale] = useState("");
  const [dataIngresso, setDataIngresso] = useState(isoDateOffset(1));
  const [rettaMensile, setRettaMensile] = useState("");
  const [riconosciIdentita, setRiconosciIdentita] = useState(true);
  const [docKeyUpload, setDocKeyUpload] = useState("auto");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    if (!open || !richiesta) return;
    const split = splitNomeOspite(richiesta.nome_ospite);
    setNome(split.nome);
    setCognome(split.cognome);
    setSesso(richiesta.sesso_ospite === "F" || richiesta.sesso_ospite === "M" ? richiesta.sesso_ospite : "");
    setDataNascita("");
    setCodiceFiscale("");
    setStatoOspite("PRE_INGRESSO");
    setDataIngresso(isoDateOffset(1));
    setRettaMensile("");
    setRiconosciIdentita(true);
    setDocKeyUpload("auto");
    setDocs(richiesta.documenti_ricevuti || []);
  }, [open, richiesta]);

  const handleStatoChange = (stato) => {
    setStatoOspite(stato);
    setDataIngresso(stato === "PRE_INGRESSO" ? isoDateOffset(1) : isoDateOffset(0));
  };

  const docsPreview = useMemo(() => docs || [], [docs]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !richiesta?.id) return;
    const formData = new FormData();
    formData.append("file", file);
    if (docKeyUpload && docKeyUpload !== "auto") {
      formData.append("doc_key", docKeyUpload);
    }
    setUploading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ospiti-rete/richieste/${richiesta.id}/documenti`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      const nuovo = res.data?.documento;
      toast.success(
        nuovo?.riconosciuto_automaticamente
          ? `Documento riconosciuto: ${nuovo.label}`
          : `Documento caricato: ${nuovo?.label || file.name}`
      );
      const dett = await axios.get(`${API_URL}/api/ospiti-rete/richieste/${richiesta.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDocs(dett.data.documenti_ricevuti || []);
      onSuccess?.({ refreshOnly: true, richiesta: dett.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento del documento");
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!nome.trim() || !cognome.trim()) {
      toast.error("Nome e cognome ospite sono obbligatori");
      return;
    }
    if (!dataIngresso) {
      toast.error("Indica la data di ingresso");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ospiti-rete/richieste/${richiesta.id}/conferma-ingresso`,
        {
          stato_ospite: statoOspite,
          nome: nome.trim(),
          cognome: cognome.trim(),
          sesso: sesso || null,
          data_nascita: dataNascita || null,
          codice_fiscale: codiceFiscale.trim() || null,
          data_ingresso: dataIngresso,
          retta_mensile: rettaMensile ? parseFloat(rettaMensile) : null,
          riconosci_identita: riconosciIdentita,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const nDocs = res.data.documenti_catalogati || 0;
      const statoLabel = res.data.stato_ospite === "PRE_INGRESSO" ? "pre-ingresso" : "ingresso";
      toast.success(
        `Fascicolo creato in ${statoLabel}`
        + (nDocs ? ` — ${nDocs} document${nDocs === 1 ? "o catalogato" : "i catalogati"}` : "")
      );
      if (res.data.identita_riconosciuta) {
        toast.success("Dati anagrafici riconosciuti dalla carta d'identità");
      }
      onOpenChange(false);
      onSuccess?.({ fascicoloId: res.data.fascicolo_id });
      if (res.data.fascicolo_id) {
        navigate(`/fascicoli-assistenziali/${res.data.fascicolo_id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione del fascicolo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="w-5 h-5 text-emerald-600" />
            Conferma ingresso e crea fascicolo
          </DialogTitle>
          <DialogDescription>
            Crea il fascicolo assistenziale in pre-ingresso o ingresso e cataloga automaticamente i documenti nella sezione corretta.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleStatoChange("PRE_INGRESSO")}
              className={`p-3 rounded-xl border text-left transition-colors ${
                statoOspite === "PRE_INGRESSO"
                  ? "border-amber-400 bg-amber-50 ring-1 ring-amber-200"
                  : "border-slate-200 hover:border-amber-200"
              }`}
            >
              <div className="flex items-center gap-2 font-semibold text-sm text-slate-800">
                <ClipboardCheck className="w-4 h-4 text-amber-600" />
                Pre-ingresso
              </div>
              <p className="text-xs text-slate-500 mt-1">
                L&apos;ospite è accettato ma non ancora in struttura. Data di ingresso futura.
              </p>
            </button>
            <button
              type="button"
              onClick={() => handleStatoChange("ATTIVO")}
              className={`p-3 rounded-xl border text-left transition-colors ${
                statoOspite === "ATTIVO"
                  ? "border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200"
                  : "border-slate-200 hover:border-emerald-200"
              }`}
            >
              <div className="flex items-center gap-2 font-semibold text-sm text-slate-800">
                <UserPlus className="w-4 h-4 text-emerald-600" />
                Ingresso
              </div>
              <p className="text-xs text-slate-500 mt-1">
                L&apos;ospite entra oggi (o in data già trascorsa) e il fascicolo risulta attivo.
              </p>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Nome ospite *</Label>
              <Input value={nome} onChange={(e) => setNome(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Cognome ospite *</Label>
              <Input value={cognome} onChange={(e) => setCognome(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Sesso</Label>
              <Select value={sesso || undefined} onValueChange={setSesso}>
                <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Maschio</SelectItem>
                  <SelectItem value="F">Femmina</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Data di nascita</Label>
              <Input type="date" value={dataNascita} onChange={(e) => setDataNascita(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Codice fiscale</Label>
              <Input
                value={codiceFiscale}
                onChange={(e) => setCodiceFiscale(e.target.value.toUpperCase())}
                placeholder="Opzionale — riconosciuto dalla CI se presente"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{statoOspite === "PRE_INGRESSO" ? "Data ingresso prevista *" : "Data ingresso *"}</Label>
              <Input type="date" value={dataIngresso} onChange={(e) => setDataIngresso(e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Retta mensile (opzionale)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={rettaMensile}
                onChange={(e) => setRettaMensile(e.target.value)}
                placeholder="Es. 2200"
              />
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-semibold text-slate-800">Documenti da catalogare</h4>
              <span className="text-xs text-slate-400">{docsPreview.length} file</span>
            </div>
            {docsPreview.length === 0 ? (
              <p className="text-xs text-slate-400">Nessun documento ancora. Puoi caricarli qui sotto prima di confermare.</p>
            ) : (
              <div className="space-y-1.5">
                {docsPreview.map((doc) => (
                  <div key={doc.file_id} className="flex items-center justify-between gap-2 text-xs bg-slate-50 rounded-lg px-3 py-2">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-700 truncate">{doc.label || doc.doc_key}</p>
                      <p className="text-slate-400 truncate">{doc.filename_originale}</p>
                    </div>
                    <span className="shrink-0 px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600">
                      {SEZIONE_LABEL[doc.sezione_fascicolo] || SEZIONE_LABEL[doc.sezione] || "Catalogazione auto"}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2 pt-1 border-t border-slate-100">
              <Label className="text-xs">Aggiungi documento</Label>
              <Select value={docKeyUpload} onValueChange={setDocKeyUpload}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto" className="text-xs">Riconosci automaticamente</SelectItem>
                  {DOCUMENTI_CATALOG.map((d) => (
                    <SelectItem key={d.key} value={d.key} className="text-xs">{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FilePickerButton
                accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.doc,.docx"
                onChange={handleUpload}
                disabled={uploading}
                fileLabel={uploading ? "Caricamento..." : "Carica file"}
                photoLabel="Scatta foto"
              />
              {uploading && (
                <p className="text-xs text-slate-400 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Riconoscimento e catalogazione in corso
                </p>
              )}
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm text-slate-700">
            <Checkbox checked={riconosciIdentita} onCheckedChange={(v) => setRiconosciIdentita(!!v)} className="mt-0.5" />
            <span className="flex items-start gap-1.5">
              <Sparkles className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
              Se è presente la carta d&apos;identità, riconosci i dati anagrafici e compilali nel fascicolo
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annulla</Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
            onClick={handleConfirm}
            disabled={saving}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />}
            {statoOspite === "PRE_INGRESSO" ? "Conferma pre-ingresso e crea fascicolo" : "Conferma ingresso e crea fascicolo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
